import React from 'react';
import styled from 'styled-components';
import IBAN from 'iban';

import { useAppDispatch, useAppSelector } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';

import './index.scss';
import {
  getBetLionWithdrawDetails,
  getDepositWager,
  getPaymentMethods,
  getTax,
  linkIban,
  withdraw,
} from '@/modules/casino/store/actions/withdrawals';
import {
  BETLION_WITHDRAW_CHANNELS,
  paymentProviderNameByWithdrawId,
  PaymentProviderWithdraw,
  WITHDRAWAL_CHANNEL_STATUS_USER_CAN_WITHDRAW,
} from '@/constants/paymentProvider';
import { requestDocuments } from '@/modules/casino/store/actions/documents';
import { receivedWithdraw, resetWithdraw } from '@/modules/casino/store/actions/withdraw_wizard';
import { useLocation } from 'react-router-dom';
import { betLionGetChannels } from '@/modules/casino/store/actions/deposit';
import { getCurrencyNumberInfo, getFormattedCurrencyAmount } from '@/page-components/utils/functions';
import { extractNumberFromFormattedString } from '@/components/modules/deposit/deposit-legacy/functions';
import { trackCustomMetaEvent } from '@/analytics';
import { freeBetsFetch, freeSpinsFetch } from '@/modules/casino/store/actions';
import ClientDetection from '@/modules/casino/utils/ClientDetection';

type WithdrawProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

type ModuleStateProps = {
  activePaymentProviders: any[];
  withdrawValue: number;
  limitError: string | boolean;
  withdrawValueNoTax: number;
  currentStep: string;
  lastStep: string;
  withdrawType: number;
  totalBonus?: number;
  selectedWithdrawType?: any;
  initialized: boolean;
  paymentMethods: any[];
  error?: string;
  isOpen?: boolean;
  loading?: boolean;
  iban: {
    details: {
      iban: string;
      beneficiaryName: string;
      friendlyName: string;
    };
    errors: {
      iban: boolean;
      beneficiaryName: boolean;
      friendlyName: boolean;
    };
  };
  // currencyPosition: string;
  focusedInput: boolean;
  trigger?: string;
  isUserDefinedAmount: boolean;
  blockerInfo: {
    loading: {
      [key: string]: boolean;
    };
    blockedBy: {
      [key: string]: boolean;
    }
  }
  loadingPaymentMethods?: boolean;
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);



const Withdraw = (componentProps: WithdrawProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;

  const blockers = React.useMemo(() => {
    const popupSteps: string[] = [];
    const info = {
      useKYC: window.config?.withdrawSettings?.ignoreKYC !== '1',
      useDocuments: window.config?.withdrawSettings?.ignoreDocuments !== '1',
      useBonusActive: window.config?.withdrawSettings?.ignoreActiveBonuses !== '1',
      useDepositWagering: window.config?.withdrawSettings?.ignoreDepositWagering !== '1',
      useTaxes: window.config?.withdrawSettings?.skipTaxStep !== '1',
      loading: {
        kyc: false,
        documents: false,
        bonusActive: false,
        depositWagering: false,
      }
    };
    if (info.useKYC) {
      popupSteps.push('kyc');
    }
    if (info.useDocuments) {
      popupSteps.push('documents');
    }
    if(info.useBonusActive) {
      popupSteps.push('bonus-active');
    }
    if(info.useDepositWagering) {
      popupSteps.push('deposit-wager');
    }

    return { popupSteps, info };
  },[]);

  const ERRORS = {
    MINIMUM_LIMIT: 'WITHDRAWALS_AMOUNT_ERROR_MINIMUM_LIMIT_{{MIN}}',
    MAXIMUM_LIMIT: 'WITHDRAWALS_AMOUNT_ERROR_MAXIMUM_LIMIT_{{MAX}}',
  };

  const dispatch = useAppDispatch();
  const wallet = useAppSelector((state) => state.wallet);
  const freeBets = useAppSelector((state) => state.freeBets);
  const freeSpins = useAppSelector((state) => state.freeSpins);
  const { errorCode, transactionId } = useAppSelector((state) => state.withdrawWizard);
  const { betlion, taxes } = useAppSelector((state) => state.withdrawals);
  const profile = useAppSelector((state) => state.profile);
  const documents = useAppSelector((state) => state.documents);
  const paymentMethods = useAppSelector((state) => state.withdrawals.paymentMethods);
  const loadingPaymentMethods = useAppSelector((state) => state.withdrawals.loadingPaymentMethods);
  const wager = useAppSelector((state) => state.withdrawals.wager);
  const currencyCode = useAppSelector((state) => state.currencies.currentCurrency);
  const [channelInfo, setChannelInfo] = React.useState<any>([]);
  // const currency = formatCurrency(currencyCode);
  const currency = currencyCode;
  // params
  const location = useLocation(); // used with new URLSearchParams() (/deposit?param=value)

  const initialState:ModuleStateProps = {
    activePaymentProviders: [],
    withdrawValue: 0,
    limitError: false,
    withdrawValueNoTax: 0,
    currentStep: 'default',
    lastStep: 'default',
    withdrawType: 0,
    totalBonus: 0,
    selectedWithdrawType: null,
    initialized: false,
    paymentMethods: [],
    error: '',
    isOpen: false,
    loading: true,
    iban: {
      details: {
        iban: '',
        beneficiaryName: '',
        friendlyName: '',
      },
      errors: {
        iban: false,
        beneficiaryName: false,
        friendlyName: false,
      },
    },
    trigger: 'initialState',
    focusedInput: false,
    // currencyPosition: 'left',
    isUserDefinedAmount: false,
    blockerInfo: {
      loading: blockers.info.loading,
      blockedBy: {
        // dynamic blocker info based on what is active
      }
    },
  };

  const [state, _setState] = React.useState<ModuleStateProps>(initialState);

  const stateRef = React.useRef(state);

  const setState = React.useCallback((data: any) => {
    stateRef.current = data;
    //const info = typeof data === 'function' ? data(state) : data;
    //console.warn('event',info.trigger, info.blockerInfo.blockedBy, info.currentStep);
      _setState(data);
  },[]);

  const setActivePaymentProviders = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);

    let selectedProviders = searchParams.getAll('providerId[]');
    if (selectedProviders.length === 0) {
      // if no providerIds[] in url, add all defined in PaymentProviderWithdraw
      selectedProviders = Object.values(PaymentProviderWithdraw).map((v) => v.toString());
    }

    const activePaymentProviders: any[] = [];

    // intersect selectedProviders with activePaymentProviders
    selectedProviders.forEach((provider) => {
      const providerConfig = window.config?.withdrawSettings?.providers?.[parseInt(provider, 10)];
      if (providerConfig?.isActive === '1') {
        if (parseInt(provider, 10) === PaymentProviderWithdraw.betlion) {
          if (betlion.channels === null) {
            dispatch(betLionGetChannels(BETLION_WITHDRAW_CHANNELS));
          } else {
            betlion.channels.forEach((channel: any) => {
              if (channel.channel_id === 'mobile') {
                activePaymentProviders.push({
                  id: parseInt(provider, 10),
                  info: {
                    ...channel,
                    mno: {
                      player: betlion.mno.player,
                      // map others with value: value
                      others: betlion.mno.other,
                    },
                  },
                });
              } else if (PaymentProviderWithdraw[`betlion_${channel.channel_id}`]) {
                const skipForNow =
                  !!window.config.withdrawSettings?.providers?.[
                    PaymentProviderWithdraw[`betlion_${channel.channel_id}`]
                  ];
                if (!skipForNow) {
                  activePaymentProviders.push({
                    id: PaymentProviderWithdraw[`betlion_${channel.channel_id}`],
                    info: channel,
                  });
                } else {
                  if (!channelInfo[PaymentProviderWithdraw[`betlion_${channel.channel_id}`]]) {
                    setChannelInfo({
                      ...channelInfo,
                      [PaymentProviderWithdraw[`betlion_${channel.channel_id}`]]: channel,
                    });
                  }
                }
              }
            });
          }
        } else {
          const activePaymentProvider: { id: number; info?: any } = { id: parseInt(provider, 10) };
          if (channelInfo[activePaymentProvider.id]) {
            activePaymentProvider.info = channelInfo[activePaymentProvider.id];
          }
          activePaymentProviders.push(activePaymentProvider);
        }
      }
    });

    // sort activePaymentProviders by window.config.withdrawSettings.providers[provider].order
    activePaymentProviders.sort((a, b) => {
      const orderA = window.config?.withdrawSettings?.providers?.[a.id]?.order
        ? parseInt(window.config.withdrawSettings.providers[a.id].order)
        : 9999;
      const orderB = window.config?.withdrawSettings?.providers?.[b.id]?.order
        ? parseInt(window.config.withdrawSettings.providers[b.id].order)
        : 9999;
      return orderA - orderB;
    });

    setState((v: ModuleStateProps) => ({
      ...v,
      activePaymentProviders: activePaymentProviders,
      trigger: 'setActivePaymentProviders',
    }));
  },[betlion.channels, betlion.mno.other, betlion.mno.player, channelInfo, dispatch, location.search,setState]);

  const getSelectedPaymentMethod = React.useCallback((providerType: any = false) => {
    if (providerType) {
      return state.paymentMethods.find((item) => item.type === providerType);
    }
    return state.paymentMethods.find((item) => item.type === state.withdrawType);
  },[state.paymentMethods, state.withdrawType]);

  const getMaxLimit = React.useCallback(() => {
    let max: number = Number(
      window.config.withdrawSettings?.providers?.[state.withdrawType]?.defaultLimits?.[currencyCode]?.max ??
        window.config?.withdrawSettings?.defaultLimits?.[currencyCode]?.max ??
        0,
    );

    switch (state.withdrawType) {
      case PaymentProviderWithdraw.betlion:
      case PaymentProviderWithdraw.betlion_unitel:
      case PaymentProviderWithdraw.betlion_telopay:
      case PaymentProviderWithdraw.betlion_afrimoney:
      case PaymentProviderWithdraw.betlion_ekwanza:
      case PaymentProviderWithdraw.betlion_aki:
      case PaymentProviderWithdraw.betlion_paypay:
      case PaymentProviderWithdraw.betlion_atlantico:
      case PaymentProviderWithdraw.betlion_other_bank_account:
      case PaymentProviderWithdraw.betlion_paychangu:
        const method = getSelectedPaymentMethod();
        if (method) {
          const channel = method.info;
          if (channel?.ignore_global_limits) {
            max = Number(channel.limits.max_amount) / 100; // force limits
          } else if (channel?.limits?.max_amount) {
            const max_amount = Number(channel.limits.max_amount) / 100;
            max = max_amount < max ? max_amount : max;
          }
        }
        break;
      default: {
        break;
      }
    }

    return max > wallet.main ? wallet.main : max;
  },[currencyCode, getSelectedPaymentMethod, state.withdrawType, wallet.main]);

  const getMinLimit = React.useCallback(() => {
    let minLimit: number = Number(
      window.config?.withdrawSettings?.providers?.[state.withdrawType]?.defaultLimits?.[currencyCode]?.min ??
        window.config?.withdrawSettings?.defaultLimits?.[currencyCode]?.min ??
        0,
    );
    switch (state.withdrawType) {
      case PaymentProviderWithdraw.betlion:
      case PaymentProviderWithdraw.betlion_unitel:
      case PaymentProviderWithdraw.betlion_telopay:
      case PaymentProviderWithdraw.betlion_afrimoney:
      case PaymentProviderWithdraw.betlion_ekwanza:
      case PaymentProviderWithdraw.betlion_aki:
      case PaymentProviderWithdraw.betlion_paypay:
      case PaymentProviderWithdraw.betlion_atlantico:
      case PaymentProviderWithdraw.betlion_other_bank_account:
      case PaymentProviderWithdraw.betlion_paychangu:
        const method = getSelectedPaymentMethod();
        if (method) {
          const channel = method.info;
          if (channel?.limits?.min_amount) {
            const min_amount = Number(channel.limits.min_amount) / 100;
            minLimit = min_amount > minLimit ? min_amount : minLimit;
          }
        }
        break;
      default: {
        break;
      }
    }
    return minLimit;
  },[currencyCode, getSelectedPaymentMethod, state.withdrawType]);

  React.useEffect(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      ...initialState,
      trigger: 'init-useEffect-reset',
    }));
    if (window.config?.withdrawSettings?.defaultAmount?.[currencyCode]) {
      withdrawValueChangeFunction(window.config?.withdrawSettings?.defaultAmount?.[currencyCode]);
    }
    dispatch(receivedWithdraw({ reset: true }));
    // dispatch(requestBPCardToken());
    if (profile?.client_player_id) {
      init();
    }

    // setCurrencyPosition();

    return () => {
      dispatch(receivedWithdraw({ reset: true }));
      dispatch(resetWithdraw());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (profile?.client_player_id) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  React.useEffect(() => {
    if (documents.loadingDocuments) {
      return;
    }


    let documentsOK = true;
    if (blockers.info.useDocuments) {
      if (documents) {
        if (documents.documentsRequest) {
          const docs = documents.documentsRequest;
          if (docs.length > 0) {
            for (let i = 0; i < docs.length; i++) {
              if (docs[i].status !== 8 /*&& docs[i].type === 1*/) {
                documentsOK = false;
                break;
              }
            }
          }
        }
      }
    }

    setState((v: ModuleStateProps) => {
      const blockedByDocuments = !documentsOK;
      return {
        ...v,
        blockerInfo: {
          ...v.blockerInfo,
          blockedBy: {
            ...v.blockerInfo.blockedBy,
            documents: blockedByDocuments
          },
          loading: {
            ...v.blockerInfo.loading,
            documents: false
          },
        },
        lastStep: v.currentStep,
        trigger: 'documents-blocker-check',
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  React.useEffect(() => {
    if (betlion?.channels) {
      setActivePaymentProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betlion?.channels, channelInfo]);

  React.useEffect(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      loadingPaymentMethods: loadingPaymentMethods,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[loadingPaymentMethods]);

  React.useEffect(() => {
    // if we are in a blocker step, do not proceed further
    if (blockers.popupSteps.includes(state.currentStep)) {
      setState((v: ModuleStateProps) => ({
        ...v,
        loading: false,
      }));
      return;
    }
    if (state.loadingPaymentMethods === undefined || state.loadingPaymentMethods) {
      return;
    }

    let pm: any[] = [];
    const iban = state.iban.details.iban.replace(/\s/g, '');
    if (paymentMethods.length > 0) {
      pm = paymentMethods.reduce((acc, item) => {
        const type: number = getWithdrawType(item);
        let exists: any = false;

        state.activePaymentProviders.forEach((provider) => {
          if (provider.id === type) {
            exists = provider;
          }
        });
        if (!exists) {
          return acc;
        }

        if (!acc[type]) {
          acc[type] = {
            type: type,
            details: [],
            info: exists.info,
          };
        }
        const details = {
          ...item.withdrawMethodDetails,
          reference: item.withdrawTypeReference,
        };
        if(type === PaymentProviderWithdraw.bridger_apple_pay) {
          details.cardBrand = 'apple-pay';
        }
        acc[type].details.push({
          ...details
        });

        if (type === PaymentProviderWithdraw.iban && iban) {
          if (item.withdrawMethodDetails?.iban === iban) {
            setState((v: ModuleStateProps) => ({
              ...v,
              selectedWithdrawType: item.withdrawTypeReference,
              trigger: 'iban-match',
            }));
          }
        }
        return acc;
      }, []);
    }

    const sortedPM: any[] = [];

    state.activePaymentProviders.forEach((provider) => {
      if (pm[provider.id]) {
        if (pm[provider.id]?.details?.length > 0) {
          sortedPM.push({ ...pm[provider.id], add: true, info: provider.info });
        } else if (window.config?.withdrawSettings?.providers?.[provider.id]?.showAlways === '1') {
          sortedPM.push({ ...pm[provider.id], add: true, info: provider.info });
        }
      } else if (window.config?.withdrawSettings?.providers?.[provider.id]?.showAlways === '1') {
        sortedPM.push({
          type: provider.id,
          details: [],
          add: true,
          info: provider.info,
        });
      }
    });

    setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: !sortedPM.length && !paymentMethods.length ? 'no-payment-methods' : 'default',
        paymentMethods: sortedPM,
        loading: false,
        trigger: 'paymentMethods-sorted',
      }));

    if (state.currentStep === 'default' || state.currentStep === 'no-payment-methods') {
      let newProvider = null;
      if (sortedPM.length > 0) {
        newProvider = sortedPM[0].type;
      }
      changePaymentProvider(newProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loadingPaymentMethods, state.activePaymentProviders]);

  React.useEffect(() => {
    if (blockers.info.useBonusActive && !freeBets.loading && !freeSpins.loading) {
      let totalBonus = wallet.bonus ? wallet.bonus : 0;
      freeBets.freeBets.forEach((fb) => {
        totalBonus += fb.count * (fb.amount_small === 1 ? fb.amount / 100 : fb.amount);
      });

      if (totalBonus > 0 || freeSpins.freeSpins.length > 0) {
        setState((v: ModuleStateProps) => ({
          ...v,
          blockerInfo: {
            ...v.blockerInfo,
            blockedBy: {...v.blockerInfo.blockedBy, bonusActive: true },
            loading: { ...v.blockerInfo.loading, bonusActive: false},
          },
          totalBonus: totalBonus,
          trigger: 'bonus-active',
        }));
      } else {
        setState((v: ModuleStateProps) => ({
          ...v,
          blockerInfo: {
            ...v.blockerInfo,
            blockedBy: {...v.blockerInfo.blockedBy, bonusActive: false },
            loading: { ...v.blockerInfo.loading, bonusActive: false},
          },
          totalBonus: totalBonus,
          trigger: 'bonus-inactive',
        }));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.bonus, freeBets.loading,freeSpins.loading]);

  React.useEffect(() => {

    if (blockers.info.useDepositWagering && wager !== undefined) {
        setState((v: ModuleStateProps) => ({
          ...v,
          blockerInfo: {
            ...v.blockerInfo,
            blockedBy: { ...v.blockerInfo.blockedBy, depositWagering: !wager.canWithdraw },
            loading: { ...v.blockerInfo.loading, depositWagering: false },
          },
          trigger: 'wager-blocker-check',
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[wager]);

  const blockersLoading = React.useMemo(() => Object.values(state.blockerInfo.loading).some((isLoading) => isLoading), [state.blockerInfo.loading]);

  React.useEffect(() => {

    // check if no blockers loading
    if (!blockersLoading) {
      let blockedStep:string|false = false;
      // prioritize blockers
      if(state.blockerInfo.blockedBy.kyc) {
        blockedStep = 'kyc';
      } else if(state.blockerInfo.blockedBy.documents) {
        blockedStep = 'documents';
      } else if (state.blockerInfo.blockedBy.depositWagering) {
        blockedStep = 'deposit-wager';
      } else  if(state.blockerInfo.blockedBy.bonusActive) {
        blockedStep = 'bonus-active';
      } else {
        Object.keys(state.blockerInfo.blockedBy).some((key: any) => {
          if (state.blockerInfo.blockedBy[key] && !blockedStep) {
            blockedStep = key;
            return true;
          }
          return false;
        });
      }
      if (
        blockedStep &&
        state.currentStep !== blockedStep &&
        blockers.popupSteps.includes(blockedStep)
      ) {
        //console.warn('changing step',blockedStep,state);
        setState((v: ModuleStateProps) => ({
          ...v,
          currentStep: blockedStep,
          lastStep: v.currentStep,
          trigger: 'blocker-found-useEffect',

        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockersLoading, state.blockerInfo.blockedBy]);

  React.useEffect(() => {
    if (state.currentStep === 'betlion') {
      withdrawBetlion();
    } else if (
      state.currentStep === 'taxes' &&
      (taxes[state.withdrawValue] == 0 ||
        typeof taxes[state.withdrawValue] === 'undefined' ||
        !blockers.info.useTaxes)
    ) {
      acceptTaxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep]);

  React.useEffect(() => {
    if (errorCode) {
      setState((v: ModuleStateProps) => ({
        ...v,
        error: errorCode === null ? '' : errorCode,
        trigger: 'errorCode-useEffect',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  React.useEffect(() => {
    if (state.error !== '') {
      if (state.error) {
        setState((v: ModuleStateProps) => ({
          ...v,
          currentStep: 'error',
          lastStep: v.currentStep,
          loading: false,
          trigger: 'found_state.error-useEffect',
        }));
      } else {
        setState((v: ModuleStateProps) => ({
          ...v,
          currentStep: v.currentStep === 'add-bank-transfer' ? 'bank-transfer' : 'success',
          lastStep: v.currentStep,
          loading: false,
          error: '',
          trigger: 'no-state.error-useEffect',
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  React.useEffect(() => {
    if (transactionId) {
      if (!errorCode) {
        setState((v: ModuleStateProps) => ({
          ...v,
          currentStep: 'success',
          lastStep: v.currentStep,
          loading: false,
          trigger: 'transactionId-useEffect',
        }));

        // TODO: also add custom logWithdrawalEvent
        trackCustomMetaEvent('web_withdrawal', { amount: state.withdrawValue });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  React.useEffect(() => {
    if (betlion.withdraw_reference_id) {
      setState((v: ModuleStateProps) => ({
        ...v,
        selectedWithdrawType: betlion.withdraw_reference_id,
        trigger: 'betlion.withdraw_reference_id-useEffect',
        loading: true,
      }));
      dispatch(withdraw(state.withdrawValue, PaymentProviderWithdraw.betlion, betlion.withdraw_reference_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betlion.withdraw_reference_id]);

  React.useEffect(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      limitError: getLimitError(state.withdrawValue),
      trigger: 'wallet-useEffect',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.total, state.withdrawValue]);

  const withdrawBetlion = React.useCallback(() => {
    // get payment method by type
    const paymentMethod = getSelectedPaymentMethod();
    if (!paymentMethod) {
      return;
    }
    if (paymentMethod.details.length === 1) {
      setState((v: ModuleStateProps) => ({
        ...v,
        selectedWithdrawType: paymentMethod.details[0].reference,
        trigger: 'withdrawBetlion-selectedWithdrawType',
        loading: true,
      }));
      dispatch(withdraw(state.withdrawValue, PaymentProviderWithdraw.betlion, paymentMethod.details[0].reference));
    } else {
      setState((v: ModuleStateProps) => ({
        ...v,
        trigger: 'withdrawBetlion-else',
        loading: true,
      }));
      dispatch(getBetLionWithdrawDetails({ channelId: paymentMethod.info.channel_id }));
    }
    // get payment method details
  },[dispatch, getSelectedPaymentMethod, state.withdrawValue,setState]);

  const getWithdrawType = React.useCallback((item: any) => {
    const type = parseInt(item.withdrawType, 10);
    if (type === PaymentProviderWithdraw.betlion) {
      if (item.withdrawMethodDetails?.channelId === 'mobile') {
        return PaymentProviderWithdraw.betlion;
      } else {
        if (PaymentProviderWithdraw[`betlion_${item.withdrawMethodDetails?.channelId}`]) {
          return PaymentProviderWithdraw[`betlion_${item.withdrawMethodDetails?.channelId}`];
        } else {
          return 0;
        }
      }
    } else if (type === PaymentProviderWithdraw.bridger) {
      if (item.withdrawMethodDetails?.cardType === 'apple-wallet') {
        return PaymentProviderWithdraw.bridger_apple_pay;
      } else if(item.withdrawMethodDetails?.cardType === 'credit-card') {
        return PaymentProviderWithdraw.bridger;
      } else {
        return 0;
      }
    }
    return type;
  },[]);

  const initBlockers = React.useCallback(() => {

    setState((v: ModuleStateProps) => ({
      ...v,
      blockerInfo: {
        ...v.blockerInfo,
        loading: {
          ...v.blockerInfo.loading,
          kyc: false,
          bonusActive: blockers.info.useBonusActive,
          documents: blockers.info.useDocuments,
          depositWagering: blockers.info.useDepositWagering,
        },
        blockedBy: {
          // ...v.blockerInfo.blockedBy,
          kyc: blockers.info.useKYC && (profile.verified != 1 || profile.account_sub_status !== 3),
        }
      },
      trigger: 'initBlockers-useEffect',
    }));
    if (blockers.info.useBonusActive) {
      dispatch(freeBetsFetch());
      dispatch(freeSpinsFetch());
    }

    if (blockers.info.useDocuments) {
      dispatch(requestDocuments());
    }
    if (blockers.info.useDepositWagering) {
      dispatch(getDepositWager());
    }
  },[blockers.info.useBonusActive, blockers.info.useDepositWagering, blockers.info.useDocuments, blockers.info.useKYC, dispatch, profile.account_sub_status, profile.verified, setState]);

  const init = React.useCallback(() => {
    if (state.initialized) {
      return;
    }
    setState((v: ModuleStateProps) => ({
          ...v,
          initialized: true,
          loading: true,
          totalBonus: 0,
          lastStep: v.currentStep,
          trigger: 'init',
    }));

    setActivePaymentProviders();
    dispatch(getPaymentMethods());


    initBlockers();

  },[state.initialized, setState, setActivePaymentProviders, dispatch,initBlockers]);

  const acceptTaxes = React.useCallback(() => {
    const st = {
      selectedWithdrawType: state.paymentMethods[state.withdrawType]?.details?.[0]?.reference,
    };
    // @ts-ignore
    switch (parseInt(state.withdrawType, 10)) {
      case PaymentProviderWithdraw.bridger:
      case PaymentProviderWithdraw.bridger_apple_pay:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'bridger',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-bridger',
        }));
        break;
      case PaymentProviderWithdraw.viva:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'viva',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-viva',
        }));
        break;
      case PaymentProviderWithdraw.iban:
      case PaymentProviderWithdraw.betlion_atlantico:
      case PaymentProviderWithdraw.betlion_other_bank_account:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'bank-transfer',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-iban',
        }));
        break;
      case PaymentProviderWithdraw.skrill:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'skrill',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-skrill',
        }));
        break;
      case PaymentProviderWithdraw.aircash_pay:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'aircash-pay',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-aircash-pay',
        }));
        break;
      case PaymentProviderWithdraw.betlion:
      case PaymentProviderWithdraw.betlion_unitel:
      case PaymentProviderWithdraw.betlion_telopay:
      case PaymentProviderWithdraw.betlion_afrimoney:
      case PaymentProviderWithdraw.betlion_ekwanza:
      case PaymentProviderWithdraw.betlion_aki:
      case PaymentProviderWithdraw.betlion_paypay:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'betlion',
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-betlion',
        }));
        break;
      case PaymentProviderWithdraw.betlion_paychangu:
        setState((v: ModuleStateProps) => ({
          ...v,
          ...st,
          currentStep: 'confirm-bank-transfer', // TODO: create this!
          lastStep: v.currentStep,
          modalOpen: false,
          trigger: 'acceptTaxes-paychangu',
        }));
        break;
      default:
        break;
    }
  },[setState, state.paymentMethods, state.withdrawType]);

  const getLimitError = React.useCallback((value: number) => {
    const min: any = getMinLimit();
    const max = getMaxLimit();
    return Number(value) < Number(min)
      ? ERRORS.MINIMUM_LIMIT
      : Number(value) > Number(max)
        ? `${ERRORS.MAXIMUM_LIMIT}`
        : false;
  },[ERRORS.MAXIMUM_LIMIT, ERRORS.MINIMUM_LIMIT, getMaxLimit, getMinLimit]);

  const withdrawValueChangeFunction = React.useCallback((value: any) => {
    setState((v: ModuleStateProps) => ({
      ...v,
      withdrawValue: value,
      limitError: getLimitError(value),
      trigger: 'withdrawValueChangeFunction',
    }));
    if (typeof taxes[value] === 'undefined') {
      dispatch(getTax(value)); // get tax
    }
  }, [dispatch, getLimitError, setState, taxes]);

  const onAmountButtonClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget ?? (e.currentTarget as HTMLButtonElement);
    // get data-value from button
    const value = el.getAttribute('data-value');
    if (value) {
      withdrawValueChangeFunction(value);
    }

    setState((v: ModuleStateProps) => ({
      ...v,
      isUserDefinedAmount: false,
      trigger: 'onAmountButtonClick',
    }));
  },[setState, withdrawValueChangeFunction]);

  const onChangeWithdrawValue = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {

    let newValue: number = 0;
    if (e.target.value !== '') {
      if (state.focusedInput) {
        newValue = parseFloat(e.target.value?.replace(/[^0-9.]/g, ''));
      } else {
        newValue = extractNumberFromFormattedString(e.target.value);
      }
    }

    setState((v: ModuleStateProps) => ({
      ...v,
      isUserDefinedAmount: true,
      trigger: 'onAmountButtonClick',
    }));

    if (newValue === state.withdrawValue) {
      return;
    }

    withdrawValueChangeFunction(newValue);
  },[setState, state.focusedInput, state.withdrawValue, withdrawValueChangeFunction]);

  const getButtonValues = React.useCallback(() => {
    const values = window.config?.withdrawSettings?.defaultPresets?.[currencyCode] ?? [20, 50, 100, 200, 300, 500];
    const hideCurrency = window.config?.withdrawSettings?.hidePresetsCurrency === '1';
    return values.map((value: number) => {
      const renderedValue = getFormattedCurrencyAmount({
        locale: '',
        amount: value,
        digits: '',
        currency: hideCurrency ? '' : currencyCode,
      });

      return {
        value: value,
        // valueRendered: `${value} ${currency}`,
        valueRendered: `${renderedValue}`,
        id: value,
        onClickHandler: onAmountButtonClick,
        active: state.withdrawValue == value,
        className: 'buttons-grid-3',
        description: '',
      };
    });
  },[currencyCode, onAmountButtonClick, state.withdrawValue]);

  const changePaymentProvider = React.useCallback((withdrawType: number) => {
    setState((v: ModuleStateProps) => {
      if (v.withdrawType == withdrawType) {
        return v;
      }
      return {
        ...v,
        withdrawType: withdrawType,
        isOpen: true,
        trigger: 'changePaymentProvider',
      };
    });
  },[setState]);

  const confirmValue = React.useCallback(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      currentStep: 'taxes',
      lastStep: v.currentStep,
      trigger: 'confirmValue',
    }));
  },[setState]);

  const onWithdraw = React.useCallback(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      loading: true,
      trigger: 'onWithdraw',
    }));
    if (state.currentStep === 'add-bank-transfer') {
      // iban without spaces
      const iban = state.iban?.details?.iban?.replace(/\s/g, '') ?? '';
      switch (state.withdrawType) {
        case PaymentProviderWithdraw.iban:
          dispatch(linkIban(iban, state.iban.details.beneficiaryName, state.iban.details.friendlyName));
          break;
        case PaymentProviderWithdraw.betlion_other_bank_account:
        case PaymentProviderWithdraw.betlion_bai_account:
          const paymentMethod = getSelectedPaymentMethod();
          if (!paymentMethod) {
            return;
          }
          dispatch(
            getBetLionWithdrawDetails({
              channelId: paymentMethod.info.channel_id,
              iban: iban,
              friendlyName: state.iban.details.beneficiaryName,
            }),
          );
          break;
      }
    } else if (state.currentStep === 'confirm-bank-transfer') {
      setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: 'betlion', // start betlion withdraw
        trigger: 'onWithdraw-confirm-bank-transfer',
      }));
    } else {
      switch (state.withdrawType) {
        case PaymentProviderWithdraw.betlion:
        case PaymentProviderWithdraw.betlion_unitel:
        case PaymentProviderWithdraw.betlion_telopay:
        case PaymentProviderWithdraw.betlion_afrimoney:
        case PaymentProviderWithdraw.betlion_ekwanza:
        case PaymentProviderWithdraw.betlion_aki:
        case PaymentProviderWithdraw.betlion_paypay:
        case PaymentProviderWithdraw.betlion_atlantico:
        case PaymentProviderWithdraw.betlion_other_bank_account:
        case PaymentProviderWithdraw.betlion_paychangu:
          dispatch(withdraw(state.withdrawValue, PaymentProviderWithdraw.betlion, state.selectedWithdrawType));
          break;
        case PaymentProviderWithdraw.bridger_apple_pay:
          dispatch(withdraw(state.withdrawValue, PaymentProviderWithdraw.bridger, state.selectedWithdrawType, ClientDetection.getAppleWithdrawalInfo()));
          break;
        default:
          dispatch(withdraw(state.withdrawValue, state.withdrawType, state.selectedWithdrawType));
      }
    }
  },[
    setState, dispatch, getSelectedPaymentMethod, state.currentStep,
    state.iban?.details?.beneficiaryName, state.iban?.details?.iban, state.iban?.details?.friendlyName,
    state.selectedWithdrawType, state.withdrawType, state.withdrawValue
  ]);

  const getPaymentProviderDetails = React.useCallback(() => {
    const data: any = [];
    // @ts-ignore
    const type = parseInt(state.withdrawType, 10); // without parseInt, it breaks in some edge cases
    // search for type in paymentMethods
    const foundIndex = state.paymentMethods.findIndex((item) => item.type === type);

    state.paymentMethods[foundIndex]?.details?.forEach((item: any) => {
      switch (type) {
        case PaymentProviderWithdraw.viva:
        case PaymentProviderWithdraw.bridger:
        case PaymentProviderWithdraw.bridger_apple_pay:
          let cardNumber = item.cardNumber;
          // format cardNumber to be displayed as **** **** **** 1234
          const cardNumberRaw = cardNumber?.replace(/\s/g, '');
          if (cardNumberRaw?.length === 16) {
            // replace first chars with *
            cardNumber = cardNumberRaw?.replace(/.{12}/, '**** **** **** ') ?? '';
          }

          if (cardNumber === '') {
            cardNumber = '**** **** **** ****';
          }

          data.push({
            name: cardNumber,
            reference: item.reference,
            description: item.cardIssuingBank,
            verified: item.verified,
            selected: state.selectedWithdrawType === item.reference,
            type: item.cardBrand ?? '',
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-viva-bridger',
              }));
            },
            cardNumber: cardNumber,
          });
          break;
        case PaymentProviderWithdraw.iban:
          data.push({
            name: item.friendlyName,
            reference: item.reference,
            description: item.iban,
            selected: state.selectedWithdrawType === item.reference,
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-iban',
              }));
            },
            cardNumber: item.friendlyName,
          });
          break;
        case PaymentProviderWithdraw.betlion_atlantico:
        case PaymentProviderWithdraw.betlion_other_bank_account:
          data.push({
            name: item.friendlyName,
            reference: item.reference,
            description: item.iban,
            selected: state.selectedWithdrawType === item.reference,
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-betlion_atlantico-betlion_other_bank_account',
              }));
            },
            cardNumber: item.friendlyName,
          });
          break;
        case PaymentProviderWithdraw.skrill:
          data.push({
            name: item.email,
            reference: item.reference,
            description: '',
            selected: state.selectedWithdrawType === item.reference,
            type: 'paysafecard',
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-skrill',
              }));
            },
            cardNumber: item.email,
          });
          break;
        case PaymentProviderWithdraw.aircash_pay:

          data.push({
            name: `+${item.aircashPhone}`,
            reference: item.reference,
            description: '',
            selected: state.selectedWithdrawType === item.reference,
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-aircashPay',
              }));
            },
            cardNumber: `+${item.aircashPhone}`,
            type: 'aircash-pay',
          });
          break;
        case PaymentProviderWithdraw.betlion_paychangu:
          data.push({
            name: item.friendlyName,
            reference: item.reference,
            description: item.iban,
            selected: state.selectedWithdrawType === item.reference,
            onClick: () => {
              setState((v: ModuleStateProps) => ({
                ...v,
                selectedWithdrawType: item.reference,
                trigger: 'getPaymentProviderDetails-betlion_paychangu',
              }));
            },
            cardNumber: state.paymentMethods[foundIndex]?.info?.bank_account?.bank_name ?? '',
          });
          break;
      }
    });

    return data;
  },[setState, state.paymentMethods, state.selectedWithdrawType, state.withdrawType]);

  const onInputFocus = React.useCallback(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      focusedInput: true,
      trigger: 'onInputFocus',
    }));
  }, [setState]);

  const onInputBlur = React.useCallback(() => {
    setState((v: ModuleStateProps) => ({
      ...v,
      focusedInput: false,
      // @ts-ignore
      withdrawValue: parseFloat(parseFloat(v.withdrawValue).toFixed(2)), // double parsing to ensure that we have maximum 2 decimal places
      trigger: 'onInputBlur',
    }));
  }, [setState]);

  const buttonDisabled = React.useMemo(() => {
    let buttonDisabled =
      state.withdrawValue === 0 || state.error !== '' || state.limitError || state.focusedInput || state.loading;
    if (state.currentStep !== 'default') {
      switch (state.currentStep) {
        case 'add-bank-transfer':
          buttonDisabled =
            !state.iban.details.iban ||
            !state.iban.details.beneficiaryName ||
            state.iban.errors.iban ||
            state.iban.errors.beneficiaryName;
          break;
        case 'confirm-bank-transfer':
          buttonDisabled = false;
          break;
        default:
          buttonDisabled = !state.selectedWithdrawType || state.selectedWithdrawType === 'undefined';
          break;
      }
    }
    return buttonDisabled;
  }, [
    state.currentStep,
    state.error,
    state.focusedInput,
    state.iban.details.beneficiaryName,
    state.iban.details.iban,
    state.iban.errors.beneficiaryName,
    state.iban.errors.iban,
    state.limitError,
    state.loading,
    state.selectedWithdrawType,
    state.withdrawValue,
  ]);

  const max = getFormattedCurrencyAmount({ locale: '', amount: getMaxLimit(), digits: '', currency: currencyCode });
  const min = getFormattedCurrencyAmount({ locale: '', amount: getMinLimit(), digits: '', currency: currencyCode });
  const wagerRemainingAmount = getFormattedCurrencyAmount({ locale: '', amount: wager?.remainingAmount ? wager.remainingAmount/100 : 0, digits: '', currency: currencyCode });

  const translatePlaceholders = {
    MAX: max,
    MIN: min,
    WAGER_REMAINING_AMOUNT: wagerRemainingAmount,
  };

  const withdrawValue = React.useMemo(() => {
    return state.focusedInput
      ? state.withdrawValue
      : getCurrencyNumberInfo({
          locale: '',
          amount: state.withdrawValue,
          digits: '',
          currency: currencyCode,
        }).amount;
  }, [currencyCode, state.focusedInput, state.withdrawValue]);

  const contextValue = {
    ...state,
    walletAmount: wallet?.main,
    walletTotal: wallet?.total,
    walletBonus: wallet?.bonus,
    totalBonusAmount: state.totalBonus + ` ${currency}`,
    currency: currency,
    taxAmount: taxes[state.withdrawValue] ? taxes[state.withdrawValue] + ` ${currency}` : false,
    onChangeWithdrawValue: onChangeWithdrawValue,
    modalOpen: true,

    onBackHandler: () => {
      let backStep: string;
      switch (state.currentStep) {
        case 'add-bank-transfer':
          backStep = 'bank-transfer';
          break;
        default:
          backStep = 'default';
          break;
      }

      setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: backStep,
        lastStep: v.currentStep,
        trigger: 'onBackHandler',
      }));
    },

    acceptTaxesHandler: () => {
      acceptTaxes();
    },
    onToggleEmptyFn: () => {
      // empty function
    },
    paymentProviderList: state.paymentMethods.map((provider) => {
      const options: { channel?: string; bodyLayoutType?: string; mno?: { player: string; others: string[] } } = {};
      let withdrawalChannelStatus = WITHDRAWAL_CHANNEL_STATUS_USER_CAN_WITHDRAW;
      let withdrawalChannelLocked = false;

      switch (provider.type) {
        case PaymentProviderWithdraw.betlion:
          options['channel'] = provider.info.channel_id;
          options['mno'] = {
            ...provider.info.mno,
          };
          break;
        case PaymentProviderWithdraw.betlion_unitel:
        case PaymentProviderWithdraw.betlion_telopay:
        case PaymentProviderWithdraw.betlion_afrimoney:
        case PaymentProviderWithdraw.betlion_ekwanza:
        case PaymentProviderWithdraw.betlion_aki:
        case PaymentProviderWithdraw.betlion_paypay:
        case PaymentProviderWithdraw.betlion_atlantico:
        case PaymentProviderWithdraw.betlion_other_bank_account:
          options['channel'] = provider.info.channel_id;
          break;
        case PaymentProviderWithdraw.betlion_paychangu:
          if (provider.info?.channel_id) {
            options['channel'] = provider.info.channel_id;

            withdrawalChannelStatus = provider.info.status;
            withdrawalChannelLocked = provider.info.locked;
          }
          break;
      }

      return {
        id: provider.paymentProviderId,
        currentStep: `amount-${paymentProviderNameByWithdrawId[provider.type]?.toLowerCase()}`,
        buttons: getButtonValues(),
        selectedBonusInfo: null,
        bonusCounter: 0,
        taxValue: taxes[state.withdrawValue] ? taxes[state.withdrawValue] : 0,
        currency: currency,
        value: withdrawValue,
        error: state.limitError ?? state.error,
        onValueChange: onChangeWithdrawValue,
        onAmountInputFocus: onInputFocus,
        onAmountInputBlur: onInputBlur,
        // currencyPosition: state.currencyPosition,
        changePaymentProvider: () => {
          changePaymentProvider(provider.type);
        },
        status: withdrawalChannelStatus,
        locked: withdrawalChannelLocked,
        isOpen: state.withdrawType == provider.type, //&& state.isOpen,
        inputAmountDisabled: false,
        confirmButton: {
          state: buttonDisabled ? 'disabled' : 'default',
          colorMapping: buttonDisabled ? 'inverted' : 'primary',
          buttonType: 'filled',
          size: 'small',
          style: 'text',
          shadow: buttonDisabled ? 'none' : 'depth_2',
          loading: state.loading,
          ctaText: 'Withdraw',
          icon: '',
          onClick: buttonDisabled ? () => 0 : confirmValue,
        },
        options: options,
        translatePlaceholders: translatePlaceholders,
      };
    }),
    paymentProviderDetails: getPaymentProviderDetails(),
    onWithdraw: buttonDisabled ? () => 0 : onWithdraw,
    addNewIban: () => {
      setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: 'add-bank-transfer',
        lastStep: v.currentStep,
        trigger: 'addNewIban',
      }));
    },
    iban: state.iban.details.iban,
    beneficiaryName: state.iban.details.beneficiaryName,
    friendlyName: state.iban.details.friendlyName,
    ibanError: state.iban.errors.iban,
    beneficiaryNameError: state.iban.errors.beneficiaryName,
    friendlyNameError: state.iban.errors.friendlyName,
    onBeneficiaryNameChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value;
      // check if value is from minimum 2 words
      if (!value.match(/^[0-9a-zA-Z-\s'.]*$/) || value.split(' ').length < 2) {
        setState((v: ModuleStateProps) => ({
          ...v,
          iban: {
            ...v.iban,
            errors: {
              ...v.iban.errors,
              beneficiaryName: 'IBAN_INVALID_BENEFICIARY_NAME',
              iban: v.iban.details.iban ? v.iban.errors.iban : false,
            },
            details: {
              ...v.iban.details,
              beneficiaryName: value,
            },
          },
          trigger: 'onBeneficiaryNameChange',
        }));
      } else {
        setState((v: ModuleStateProps) => ({
          ...v,
          iban: {
            ...v.iban,
            errors: {
              ...v.iban.errors,
              beneficiaryName: false,
              iban: v.iban.details.iban ? v.iban.errors.iban : false,
            },
            details: {
              ...v.iban.details,
              beneficiaryName: value,
            },
          },
          trigger: 'onBeneficiaryNameChange',
        }));
      }
    },
    onFriendlyNameChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value;
      // check if value is from  minimum 2 characters
      if (!/^.{2,}$/.test(value)) {
        setState((v: ModuleStateProps) => ({
          ...v,
          iban: {
            ...v.iban,
            errors: {
              ...v.iban.errors,
              friendlyName: 'IBAN_INVALID_FRIENDLY_NAME',
            },
          },
          trigger: 'onFriendlyNameChange',
        }));
      }
    },
    onIbanChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event?.target?.value?.replace(/[^a-zA-Z0-9]/g, '');
      if (!IBAN.isValid(value)) {
        setState((v: ModuleStateProps) => ({
          ...v,
          iban: {
            ...v.iban,
            details: {
              ...v.iban.details,
              iban: value,
            },
            errors: {
              ...v.iban.errors,
              iban: 'INVALID_IBAN',
              beneficiaryName: v.iban.details.beneficiaryName ? v.iban.errors.beneficiaryName : false,
            },
          },
          trigger: 'onIbanChange',
        }));
      } else {
        setState((v: ModuleStateProps) => ({
          ...v,
          iban: {
            ...v.iban,
            details: {
              ...v.iban.details,
              iban: IBAN.printFormat(value),
            },
            errors: {
              ...v.iban.errors,
              iban: false,
            },
          },
          trigger: 'onIbanChange',
        }));
      }
    },
    addIbanButton: {
      state: buttonDisabled ? 'disabled' : 'default',
      colorMapping: buttonDisabled ? 'inverted' : 'primary',
      buttonType: 'filled',
      size: 'small',
      style: 'text',
      shadow: buttonDisabled ? 'none' : 'depth_2',
      loading: state.loading,
      ctaText: 'Add IBAN',
      icon: '',
      onClick: buttonDisabled ? () => 0 : onWithdraw,
    },

    confirmButton: {
      state: buttonDisabled ? 'disabled' : 'default',
      colorMapping: buttonDisabled ? 'inverted' : 'primary',
      buttonType: 'filled',
      size: 'small',
      style: 'text',
      shadow: buttonDisabled ? 'none' : 'depth_2',
      loading: state.loading,
      ctaText: 'Deposit',
      icon: '',
      onClick: buttonDisabled ? () => 0 : onWithdraw,
    },
    retryFn: () => {
      dispatch(receivedWithdraw({ reset: true }));
      setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: v.lastStep !== 'error' && v.lastStep !== 'success' ? v.lastStep : 'default',
        lastStep: v.currentStep,
        loading: false,
        trigger: 'retryFn',
      }));
    },
    translatePlaceholders: translatePlaceholders,
    goToPrevStep: () => {
      if (state.currentStep === 'default' || state.currentStep === 'success') return;
      setState((v: ModuleStateProps) => ({
        ...v,
        currentStep: v.lastStep,
        lastStep: 'default',
        trigger: 'goToPrevStep',
      }));
    },
    hideBackButton:
      state.currentStep === 'default' ||
      state.currentStep === 'success' ||
      state.currentStep === 'no-payment-methods' ||
      state.loading || blockersLoading ||
      blockers.popupSteps.includes(state.currentStep),
    loading: state.loading || blockersLoading,
  };

  // React.useEffect(() => {
  //   console.warn('Current step: ', state.blockerInfo.blockedBy,state.initialized,state.trigger,state);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [state.currentStep,state.blockerInfo.blockedBy]);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Withdraw;
